import { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Chip,
  Alert,
  Paper,
  Tabs,
  Tab,
  TextField,
  Avatar,
  Dialog,
  DialogContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import LayersIcon from "@mui/icons-material/Layers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuidv4 } from "uuid";

const MAX_IMAGES = 200;
const MAX_CONTENIDOS = 100;

const readFilesAsBase64 = (files) =>
  Promise.all(
    Array.from(files).map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve({ imageId: uuidv4(), fileName: file.name, base64: reader.result, type: file.type });
          reader.readAsDataURL(file);
        })
    )
  );

// ─── Drop zone ────────────────────────────────────────────────────────────────

const DropZone = ({ onDrop, disabled, label, compact }) => {
  const fileInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || !e.dataTransfer.files?.length) return;
    const images = await readFilesAsBase64(e.dataTransfer.files);
    onDrop(images);
  };

  const handleFileChange = async (e) => {
    if (!e.target.files?.length) return;
    const images = await readFilesAsBase64(e.target.files);
    onDrop(images);
    e.target.value = "";
  };

  return (
    <Paper
      variant="outlined"
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && fileInputRef.current?.click()}
      sx={{
        p: compact ? 2 : 4,
        textAlign: "center",
        borderStyle: "dashed",
        borderWidth: 2,
        borderColor: dragging ? "#1565C0" : disabled ? "#e0e0e0" : "#90caf9",
        backgroundColor: dragging ? "rgba(21,101,192,0.06)" : disabled ? "#fafafa" : "#f8fbff",
        cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: 3,
        transition: "all 0.2s",
        "&:hover": disabled ? {} : { borderColor: "#1565C0", backgroundColor: "rgba(21,101,192,0.04)" },
      }}
    >
      <CloudUploadIcon sx={{ fontSize: compact ? 28 : 40, color: dragging ? "#1565C0" : "#90caf9", mb: 1 }} />
      <Typography variant={compact ? "body2" : "subtitle1"} sx={{ fontWeight: 600, color: "#0A395F" }}>
        {dragging ? "Suelta aqui" : label || "Arrastra las imagenes aqui"}
      </Typography>
      {!compact && (
        <Typography variant="caption" color="text.secondary">o haz click para explorar</Typography>
      )}
      <input ref={fileInputRef} hidden type="file" multiple accept="image/*" onChange={handleFileChange} />
    </Paper>
  );
};

// ─── Image thumbnail ──────────────────────────────────────────────────────────

const ImageThumb = ({ image, index, onRemove, onPreview }) => (
  <Card variant="outlined" sx={{ position: "relative", borderRadius: 1.5, overflow: "hidden" }}>
    <Box
      sx={{
        position: "absolute", top: 4, left: 4, zIndex: 1,
        backgroundColor: "rgba(10,57,95,0.8)", color: "#fff",
        borderRadius: 0.75, px: 0.75, py: 0.25, fontSize: 10, fontWeight: 700,
      }}
    >
      #{index + 1}
    </Box>
    <CardMedia
      component="img"
      image={image.base64}
      alt={image.fileName}
      onClick={() => onPreview(image)}
      sx={{ height: 80, objectFit: "cover", cursor: "zoom-in" }}
    />
    <CardActions sx={{ p: 0.5, justifyContent: "space-between", backgroundColor: "#fafafa" }}>
      <Typography variant="caption" noWrap title={image.fileName} sx={{ maxWidth: 70, fontSize: 10 }}>
        {image.fileName}
      </Typography>
      <IconButton size="small" color="error" onClick={() => onRemove(image.imageId)} sx={{ p: 0.25 }}>
        <DeleteIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </CardActions>
  </Card>
);

// ─── Main component ────────────────────────────────────────────────────────────

export const StepCargar = ({ state, dispatch }) => {
  const mode = state.uploadMode || "fixed";
  const [previewImage, setPreviewImage] = useState(null);

  // ── Mode A helpers ───────────────────────────────────────────────────────────

  const handleFixedDrop = (images) => {
    dispatch({ type: "ADD_STAGING_IMAGES", payload: images });
  };

  const handleRemoveStaging = (imageId) => {
    dispatch({ type: "REMOVE_STAGING_IMAGE", payload: imageId });
  };

  // Compute preview groups from staging
  const n = state.imagesPerGroup || 1;
  const stagingImages = state.stagingImages || [];
  const previewGroups = [];
  for (let i = 0; i < stagingImages.length; i += n) {
    previewGroups.push(stagingImages.slice(i, i + n));
  }

  // ── Mode B helpers ────────────────────────────────────────────────────────────

  const handleBatchDrop = (images) => {
    if (state.contenidos.length >= MAX_CONTENIDOS) return;
    dispatch({ type: "ADD_CONTENIDO", payload: { contenidoId: uuidv4(), images } });
  };

  const handleRemoveContenido = (contenidoId) => {
    dispatch({ type: "REMOVE_CONTENIDO", payload: contenidoId });
  };

  const handleRemoveImageFromContenido = (contenidoId, imageId) => {
    dispatch({ type: "REMOVE_IMAGE_FROM_CONTENIDO", payload: { contenidoId, imageId } });
  };

  const totalImagesFixed = stagingImages.length;
  const totalContenidosBatch = state.contenidos.length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F", mb: 0.5 }}>
          Cargar imagenes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Elige como agrupar las imagenes en contenidos.
        </Typography>
      </Box>

      {/* Mode tabs */}
      <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <Tabs
          value={mode}
          onChange={(_, v) => dispatch({ type: "SET_UPLOAD_MODE", payload: v })}
          variant="fullWidth"
          sx={{
            "& .MuiTab-root": { fontSize: "0.8rem", fontWeight: 600, py: 1.5, minHeight: 48 },
            "& .MuiTab-root.Mui-selected": { color: "#1565C0" },
            "& .MuiTabs-indicator": { backgroundColor: "#1565C0" },
          }}
        >
          <Tab
            value="fixed"
            icon={<ViewModuleIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Agrupacion Fija"
          />
          <Tab
            value="batch"
            icon={<LayersIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Por Tandas"
          />
        </Tabs>
      </Paper>

      {/* ── MODE A: Fixed grouping ─────────────────────────────────────────────── */}
      {mode === "fixed" && (
        <Box>
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 2, py: 0.75, fontSize: "0.8rem" }}>
            Sube todas las imagenes de una vez. El sistema las agrupa automaticamente segun la cantidad de imagenes por contenido que definas.
          </Alert>

          {/* Images per group */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
              Imagenes por contenido:
            </Typography>
            <TextField
              type="number"
              size="small"
              value={state.imagesPerGroup || 1}
              onChange={(e) => dispatch({ type: "SET_IMAGES_PER_GROUP", payload: e.target.value })}
              inputProps={{ min: 1, max: 50 }}
              sx={{ width: 90 }}
            />
            {totalImagesFixed > 0 && (
              <Chip
                label={`${previewGroups.length} contenido${previewGroups.length !== 1 ? "s" : ""} a crear`}
                color="primary"
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          <DropZone
            onDrop={handleFixedDrop}
            disabled={totalImagesFixed >= MAX_IMAGES}
            label={totalImagesFixed > 0 ? "Agregar mas imagenes" : "Arrastra todas las imagenes aqui"}
          />

          {totalImagesFixed === 0 && (
            <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
              <AddPhotoAlternateIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2" color="text.disabled">
                Sin imagenes. Agrega al menos una para continuar.
              </Typography>
            </Box>
          )}

          {totalImagesFixed > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 1.5 }}>
                Vista previa de agrupacion — {previewGroups.length} contenido{previewGroups.length !== 1 ? "s" : ""}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {previewGroups.map((group, gIdx) => (
                  <Paper
                    key={gIdx}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2, borderColor: "#c5cae9" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Avatar sx={{ width: 22, height: 22, fontSize: 11, backgroundColor: "#1565C0" }}>
                        {gIdx + 1}
                      </Avatar>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#0A395F" }}>
                        Contenido #{gIdx + 1}
                      </Typography>
                      <Chip label={`${group.length} imagen${group.length !== 1 ? "es" : ""}`} size="small" sx={{ fontSize: 10, height: 18 }} />
                      {state.loteBase != null && (
                        <Chip label={`Lote #${state.loteBase + gIdx}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                      )}
                    </Box>
                    <Grid container spacing={1}>
                      {group.map((img, iIdx) => (
                        <Grid item key={img.imageId} xs={4} sm={3} md={2}>
                          <ImageThumb image={img} index={iIdx} onRemove={handleRemoveStaging} onPreview={setPreviewImage} />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* ── MODE B: Batch ─────────────────────────────────────────────────────── */}
      {mode === "batch" && (
        <Box>
          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, borderRadius: 2, py: 0.75, fontSize: "0.8rem" }}>
            Cada vez que arrastres un grupo de imagenes, se creara un nuevo contenido. Ideal cuando cada documento tiene distinta cantidad de paginas.
          </Alert>

          <DropZone
            onDrop={handleBatchDrop}
            disabled={totalContenidosBatch >= MAX_CONTENIDOS}
            label={totalContenidosBatch > 0 ? "Arrastrar siguiente tanda → nuevo contenido" : "Arrastra las imagenes del primer contenido"}
          />

          {totalContenidosBatch === 0 && (
            <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
              <LayersIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2" color="text.disabled">
                Sin contenidos. Arrastra la primera tanda de imagenes.
              </Typography>
            </Box>
          )}

          {totalContenidosBatch > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {totalContenidosBatch} contenido{totalContenidosBatch !== 1 ? "s" : ""} cargado{totalContenidosBatch !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {state.contenidos.map((c, cIdx) => (
                  <Paper key={c.contenidoId} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: "#c5cae9" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Avatar sx={{ width: 22, height: 22, fontSize: 11, backgroundColor: "#1565C0" }}>
                        {cIdx + 1}
                      </Avatar>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#0A395F" }}>
                        Contenido #{cIdx + 1}
                      </Typography>
                      <Chip label={`${c.images.length} imagen${c.images.length !== 1 ? "es" : ""}`} size="small" sx={{ fontSize: 10, height: 18 }} />
                      {state.loteBase != null && (
                        <Chip label={`Lote #${state.loteBase + cIdx}`} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                      )}
                      <Box sx={{ flex: 1 }} />
                      <IconButton size="small" color="error" onClick={() => handleRemoveContenido(c.contenidoId)} sx={{ p: 0.5 }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <Grid container spacing={1}>
                      {c.images.map((img, iIdx) => (
                        <Grid item key={img.imageId} xs={4} sm={3} md={2}>
                          <ImageThumb
                            image={img}
                            index={iIdx}
                            onRemove={(imageId) => handleRemoveImageFromContenido(c.contenidoId, imageId)}
                            onPreview={setPreviewImage}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
      {/* Image preview dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        PaperProps={{ sx: { backgroundColor: "#111", position: "relative" } }}
      >
        <IconButton
          onClick={() => setPreviewImage(null)}
          sx={{ position: "absolute", top: 8, right: 8, color: "#fff", zIndex: 1, backgroundColor: "rgba(0,0,0,0.4)", "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" } }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, lineHeight: 0 }}>
          {previewImage && (
            <img
              src={previewImage.base64}
              alt={previewImage.fileName}
              style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain", display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
